import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DoCheck,
  ElementRef,
  Input,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { CanColor, mixinColor } from '@angular/material/core';
import { Constructor } from '@angular/material/core/common-behaviors/constructor';

import { SummaryStatisticSet } from '@mtna/model-predefined-data-ui/public_api';

export class RdsVariableDistributionBase {
  constructor(public _elementRef: ElementRef) {}
}

export const _RdsVariableDistributionMixinBase: Constructor<CanColor> & typeof RdsVariableDistributionBase = mixinColor(
  RdsVariableDistributionBase,
  'primary'
);

/**
 * Variable Distribution Component visualizes the Variable's Summary Statistics's min, max, mean and standard deviation.
 *
 * @author Zach Edwards <zach.edwards@mtna.us>
 */

@Component({
  selector: 'rds-variable-distribution',
  templateUrl: './variable-distribution.html',
  styleUrls: ['./variable-distribution.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class RdsVariableDistributionComponent extends _RdsVariableDistributionMixinBase implements CanColor, DoCheck {
  /** Theme color, defaults to primary */
  @Input() color: 'primary' | 'accent';

  /** Required summary statistic set to display */
  @Input()
  get statSet(): SummaryStatisticSet {
    return this._statSet;
  }
  set statSet(newValue: SummaryStatisticSet) {
    this._statSet = newValue;
    this.calcMeanLeft();
    if (!!this.sdWidth) {
      this.calcSdWidth();
      this.cdr.markForCheck();
    }
  }
  private _statSet: SummaryStatisticSet;

  /** References to html element that represents the distribution line */
  @ViewChild('line') lineElem: ElementRef;

  /** Percentage from beginning of line */
  meanLeft: string;
  /** Positions mean */
  meanPositionPercent: number;
  /** Line width */
  rangePxWidth: number;
  /** Width of standard deviation */
  sdWidth: string;
  /** Clips left or right side of standard deviation */
  sdTrunc: string;

  constructor(private cdr: ChangeDetectorRef, elementRef: ElementRef) {
    super(elementRef);
  }

  ngDoCheck() {
    // If the line width has changed since the last DoCheck,
    // recalculate the SD width
    const lineWidth = !!this.lineElem ? Math.round(this.lineElem.nativeElement.getBoundingClientRect().width) : 0;
    if (!!lineWidth && lineWidth !== this.rangePxWidth) {
      this.calcSdWidth();
    }
  }

  calcMeanLeft() {
    const range = this.statSet.max - this.statSet.min;
    this.meanPositionPercent = (this.statSet.mean - this.statSet.min) / range;
    this.meanLeft = this.meanPositionPercent * 100 + '%';
  }

  calcSdWidth() {
    const range = this.statSet.max - this.statSet.min;
    // Grab line width and get distance from standard deviation
    this.rangePxWidth = !!this.lineElem ? Math.round(this.lineElem.nativeElement.getBoundingClientRect().width) : 0;
    const width = (this.statSet.standardDeviation * this.rangePxWidth) / range;
    this.sdWidth = width + 'px';
    // Range from start of line to mean
    const sdStart = this.rangePxWidth * this.meanPositionPercent - width / 2;
    // Clip when standard deviation range exceeds line boundaries
    const clipRight = this.rangePxWidth - sdStart + 'px';
    const clipLeft = sdStart < 0 ? Math.abs(sdStart) + 'px' : '-2px';
    this.sdTrunc = `rect(auto, ${clipRight}, auto, ${clipLeft})`;
    this.cdr.markForCheck();
  }
}
